import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, Clock, TrendingUp, ChevronLeft, ChevronRight, Menu, X, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [countryServices, setCountryServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchServiceDetails();
  }, [serviceId]);

  const fetchServiceDetails = async () => {
    try {
      setLoading(true);
      const [serviceRes, countryRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/country-services/by-service/${serviceId}`)
      ]);
      const foundService = serviceRes.data.find(s => s.id === serviceId);
      setService(foundService);
      setCountryServices(countryRes.data);
      
      const indices = {};
      countryRes.data.forEach(cs => {
        indices[cs.id] = 0;
      });
      setCurrentImageIndex(indices);
    } catch (error) {
      console.error('Failed to fetch service details:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextImage = (countryServiceId, totalImages) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [countryServiceId]: (prev[countryServiceId] + 1) % totalImages
    }));
  };

  const prevImage = (countryServiceId, totalImages) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [countryServiceId]: (prev[countryServiceId] - 1 + totalImages) % totalImages
    }));
  };

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="error-page">
        <h1>Service not found</h1>
        <button onClick={() => navigate('/')} className="btn-primary">Go Home</button>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-container">
          <div className="logo-section">
            <h1 className="logo-text" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>VISAASURE</h1>
          </div>

          <nav className="desktop-nav">
            <button onClick={() => navigate('/')} className="nav-link">Home</button>
            <button onClick={scrollToContact} className="nav-link">Contact</button>
          </nav>

          <div className="header-actions">
            <button onClick={() => navigate('/client-login')} className="btn-outline">
              <User size={18} />
              Client Login
            </button>
            <button onClick={scrollToContact} className="btn-primary">Book Consultation</button>
          </div>

          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="mobile-nav-link">Home</button>
            <button onClick={() => { scrollToContact(); setMobileMenuOpen(false); }} className="mobile-nav-link">Contact</button>
            <button onClick={() => { navigate('/client-login'); setMobileMenuOpen(false); }} className="mobile-nav-link">Client Login</button>
          </div>
        )}
      </header>
      
      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="container">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            Back to Services
          </button>
          
          <div className="service-detail-header">
            <h1 className="heading-hero">{service.title}</h1>
            <p className="body-large">{service.description}</p>
          </div>
        </div>
      </section>

      {/* Country Services */}
      <section className="country-services-section">
        <div className="container">
          {countryServices.length === 0 ? (
            <div className="empty-state">
              <p className="body-large">No country services available yet.</p>
              <button onClick={scrollToContact} className="btn-primary">Contact Us</button>
            </div>
          ) : (
            <>
              <div className="section-header">
                <h2 className="heading-1">Available Countries</h2>
                <p className="body-large">Select a country for visa services</p>
              </div>

              <div className="country-cards-grid">
                {countryServices.map(cs => (
                  <div key={cs.id} className={`country-card ${service.accent_color}`}>
                    {cs.images && cs.images.length > 0 && (
                      <div className="country-card-images">
                        <img 
                          src={cs.images[currentImageIndex[cs.id] || 0]} 
                          alt={cs.country_name}
                          className="country-image"
                        />
                        {cs.images.length > 1 && (
                          <>
                            <button 
                              onClick={() => prevImage(cs.id, cs.images.length)}
                              className="carousel-btn prev-btn"
                            >
                              <ChevronLeft size={24} />
                            </button>
                            <button 
                              onClick={() => nextImage(cs.id, cs.images.length)}
                              className="carousel-btn next-btn"
                            >
                              <ChevronRight size={24} />
                            </button>
                            <div className="carousel-dots">
                              {cs.images.map((_, idx) => (
                                <span 
                                  key={idx}
                                  className={`dot ${idx === (currentImageIndex[cs.id] || 0) ? 'active' : ''}`}
                                  onClick={() => setCurrentImageIndex(prev => ({...prev, [cs.id]: idx}))}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="country-card-content">
                      <h3 className="heading-2">{cs.country_name}</h3>
                      
                      <div className="visa-count-badge">
                        <CheckCircle size={20} />
                        <span><strong>{cs.visa_count}</strong> Visas Processed</span>
                      </div>

                      {cs.description && (
                        <p className="body-medium country-description">{cs.description}</p>
                      )}

                      {cs.highlights && cs.highlights.length > 0 && cs.highlights[0] && (
                        <ul className="country-highlights">
                          {cs.highlights.filter(h => h).map((highlight, idx) => (
                            <li key={idx}>
                              <CheckCircle size={16} />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="country-meta">
                        {cs.processing_time && (
                          <div className="meta-item">
                            <Clock size={16} />
                            <span>{cs.processing_time}</span>
                          </div>
                        )}
                        {cs.success_rate && (
                          <div className="meta-item">
                            <TrendingUp size={16} />
                            <span>{cs.success_rate} Success</span>
                          </div>
                        )}
                      </div>

                      <button onClick={scrollToContact} className="btn-primary country-cta">
                        {cs.button_text}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 className="heading-3 footer-title">VISAASURE</h3>
              <p className="body-small footer-description">
                Your trusted partner for visa consultation and immigration services.
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="caption">© 2024 Visaasure. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetail;
